import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Wordmark } from "@/components/Brand";
import { toast } from "sonner";
import { ShieldCheck, QrCode, LogOut } from "lucide-react";
import { RecoveryCodes } from "@/components/RecoveryCodes";

export default function MandatoryMFA({ onDone, onLogout }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);

  const begin = async () => {
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  useEffect(() => { begin(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      toast.success("Two-factor authentication enabled");
      if (data.recovery_codes) setCodes(data.recovery_codes);
      else await onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6" data-testid="mandatory-mfa">
      <div className="w-full max-w-md fadeup">
        <div className="text-center mb-6"><Wordmark className="inline-block" /></div>
        <div className="card p-8">
          {codes ? (
            <RecoveryCodes codes={codes} onDone={onDone} doneLabel="Continue to console" />
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[color:var(--sage-light)]">
                  <ShieldCheck className="text-[color:var(--olive)]" />
                </div>
                <div>
                  <h1 className="font-serif text-2xl">Secure your account</h1>
                  <p className="text-sm text-[color:var(--ink-2)]">Two-factor authentication is required for Super Admin.</p>
                </div>
              </div>
              <p className="text-sm text-[color:var(--ink-2)] mb-4">
                Scan the QR code with Google Authenticator, 1Password, or Authy, then enter the 6-digit code to finish.
                You cannot access the console until 2FA is enabled.
              </p>
              {setup ? (
                <>
                  <img src={setup.qr} alt="2FA QR" className="w-44 h-44 rounded-lg border border-[color:var(--line)] bg-white p-2 mx-auto" data-testid="mfa-qr" />
                  <p className="text-xs text-[color:var(--muted)] mt-2 font-mono break-all text-center">Secret: {setup.secret}</p>
                  <div className="flex gap-2 mt-5">
                    <input className="field font-mono tabnum text-center tracking-[0.3em]" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" data-testid="mfa-code" />
                    <button className="btn btn-primary px-6" disabled={code.length !== 6 || busy} onClick={enable} data-testid="mfa-enable">{busy ? "…" : "Enable"}</button>
                  </div>
                </>
              ) : (
                <button className="btn btn-primary w-full" onClick={begin} data-testid="mfa-setup"><QrCode size={16} /> Generate QR code</button>
              )}
              <button className="btn btn-ghost w-full mt-4 h-10 text-sm" onClick={onLogout} data-testid="mfa-logout"><LogOut size={14} /> Sign out</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
